import type { TypographyVariantsOptions } from '@mui/material/styles/createTypography';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    'text-lg-sb': true;
    'text-lg': true;
    'text-md': true;
    'text-md-sb': true;
    'text-sm': true;
    'text-sm-sb': true;
    'button-lg': true;
    'button-sm': true;
    'grey-wrapped-sm': true;
    'error-wrapped-sm': true;
    'success-wrapped-sm': true;
    'grey-wrapped-md': true;
    'error-wrapped-md': true;
    'success-wrapped-md': true;
    'primary-wrapped-sm': true;
    'primary-wrapped-md': true;
    'label-text-sm': true;
    'label-text-md': true;
    'h3-sb': true;
  }
}

/** Palette shape used by typography (theme palette after merge). */
export interface ITypographyPalette {
  font?: { primary: string; secondary: string };
  primary?: { main: string; light: string; contrastText: string };
  contentBackground?: { grey: string };
  error?: { main: string };
  success?: { main: string; contrastText: string };
  successGradient?: string;
}

export function createTypography(
  palette: ITypographyPalette,
): TypographyVariantsOptions {
  const font = palette.font ?? { primary: '#000', secondary: '#666' };
  const primary = palette.primary ?? {
    main: '#000',
    light: '#000',
    contrastText: '#fff',
  };
  const contentBackground = palette.contentBackground ?? { grey: '#f5f5f5' };
  const error = palette.error ?? { main: '#d32f2f' };
  const success = palette.success ?? { main: '#2e7d32', contrastText: '#fff' };
  const successGradient = palette.successGradient ?? success.main;

  return {
    fontFamily: ['Open Sans', 'Manrope', 'Coolvetica', 'sans-serif'].join(','),
    h1: {
      fontSize: '2rem',
      lineHeight: '2.25rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
      fontWeight: 700,
    },
    h3: {
      fontSize: '1.125rem',
      lineHeight: '1.25rem',
      fontWeight: 600,
    },
    'h3-sb': {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '0,875rem',
      lineHeight: '1.05rem',
      fontWeight: 500,
    },
    'text-lg-sb': {
      fontSize: '1rem',
      lineHeight: '1.375rem',
      fontWeight: 600,
    },
    'text-lg': {
      fontSize: '1.125rem',
      lineHeight: '1.375rem',
      fontWeight: 400,
    },
    'text-md': {
      fontSize: '1rem',
      lineHeight: '1.125rem',
      fontWeight: 400,
    },
    'text-md-sb': {
      fontSize: '1rem',
      lineHeight: '1.125rem',
      fontWeight: 600,
    },
    'text-sm': {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 400,
    },
    'text-sm-sb': {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 600,
    },
    'label-text-sm': {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 400,
      color: font.secondary,
    },
    'label-text-md': {
      fontSize: '1rem',
      lineHeight: '1.125rem',
      fontWeight: 400,
      color: font.secondary,
    },
    'button-lg': {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.2px',
    },
    'button-sm': {
      fontSize: '0.75rem',
      lineHeight: '0.75rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.2px',
    },
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.625rem',
    },
    overline: {
      color: primary.light,
      fontSize: 12,
      fontWeight: 'bold',
      letterSpacing: 0.4,
    },
    'grey-wrapped-sm': {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 400,
      padding: '4px',
      borderRadius: '8px',
      backgroundColor: contentBackground.grey,
      color: font.primary,
      width: 'fit-content',
    },
    'error-wrapped-sm': {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 400,
      borderRadius: '12px',
      backgroundColor: error.main,
      width: 'fit-content',
      padding: '4px 8px',
    },
    'success-wrapped-sm': {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 600,
      borderRadius: '12px',
      background: successGradient,
      color: success.contrastText,
      width: 'fit-content',
      padding: '4px 8px',
    },
    'grey-wrapped-md': {
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      fontWeight: 400,
      padding: '4px',
      borderRadius: '8px',
      backgroundColor: contentBackground.grey,
      color: font.primary,
      width: 'fit-content',
    },
    'error-wrapped-md': {
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      fontWeight: 400,
      padding: '4px',
      borderRadius: '8px',
      backgroundColor: error.main,
      width: 'fit-content',
    },
    'success-wrapped-md': {
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      fontWeight: 400,
      padding: '4px',
      borderRadius: '8px',
      backgroundColor: success.main,
      width: 'fit-content',
    },
    'primary-wrapped-sm': {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 600,
      borderRadius: '12px',
      backgroundColor: primary.main,
      color: primary.contrastText,
      width: 'fit-content',
      padding: '4px 8px',
    },
    'primary-wrapped-md': {
      fontSize: '1rem',
      lineHeight: '1.5rem',
      fontWeight: 600,
      borderRadius: '12px',
      backgroundColor: primary.main,
      color: primary.contrastText,
      width: 'fit-content',
      padding: '4px 8px',
    },
  } as TypographyVariantsOptions;
}
